/**
 * PortalNotifications — Notification preferences & recent conversion alerts
 *
 * Settings for email, webhook, and threshold-based notifications.
 * Also displays recent conversion events as notification cards.
 */

import React, { useState, useEffect } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { supabaseGet } from '@/utils/supabaseRest';
import { Bell, Mail, Webhook, DollarSign, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ───────────────────────────────────────

interface NotificationSettings {
  email_enabled: boolean;
  email_address: string;
  trigger_new_conversion: boolean;
  trigger_value_exceeds: boolean;
  value_threshold: number;
  trigger_daily_summary: boolean;
  trigger_weekly_report: boolean;
  webhook_url: string;
}

interface ConversionEvent {
  session_id: string;
  vertical_id: string | null;
  revenue: number | null;
  created_at: string;
}

interface VerticalRef {
  id: string;
  name: string;
}

// ─── LocalStorage helpers ────────────────────────

const LS_KEY = 'intentdrive_notifications';

function loadSettings(): NotificationSettings {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    email_enabled: false,
    email_address: '',
    trigger_new_conversion: true,
    trigger_value_exceeds: false,
    value_threshold: 50,
    trigger_daily_summary: false,
    trigger_weekly_report: false,
    webhook_url: '',
  };
}

function saveSettings(settings: NotificationSettings) {
  localStorage.setItem(LS_KEY, JSON.stringify(settings));
}

// ─── Component ───────────────────────────────────

export default function PortalNotifications() {
  const { profile } = useAuth();
  const [settings, setSettings] = useState<NotificationSettings>(() => {
    const s = loadSettings();
    if (!s.email_address && profile?.email) s.email_address = profile.email;
    return s;
  });
  const [conversions, setConversions] = useState<ConversionEvent[]>([]);
  const [verticals, setVerticals] = useState<VerticalRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [convs, verts] = await Promise.all([
          supabaseGet<ConversionEvent[]>(
            `session_events?select=session_id,vertical_id,revenue,created_at&event_type=eq.converted&order=created_at.desc&limit=10`
          ),
          supabaseGet<VerticalRef[]>('verticals?select=id,name'),
        ]);
        setConversions(convs);
        setVerticals(verts);
      } catch (e) {
        console.error('Failed to load notification data:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const verticalMap: Record<string, string> = {};
  verticals.forEach(v => { verticalMap[v.id] = v.name; });

  const update = (patch: Partial<NotificationSettings>) => {
    setSettings(prev => ({ ...prev, ...patch }));
    setSaved(false);
  };

  const handleSave = () => {
    saveSettings(settings);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <PortalLayout>
      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-emerald-400" />
          <h1 className="text-2xl font-bold">Notifications</h1>
        </div>

        {/* Settings card */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-6">
          <h2 className="text-lg font-semibold">Notification Preferences</h2>

          {/* Email toggle + address */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.email_enabled}
                onChange={e => update({ email_enabled: e.target.checked })}
                className="h-4 w-4 accent-emerald-500 rounded"
              />
              <Mail className="h-4 w-4 text-slate-400" />
              <span className="text-sm">Email notifications</span>
            </label>
            {settings.email_enabled && (
              <input
                type="email"
                value={settings.email_address}
                onChange={e => update({ email_address: e.target.value })}
                placeholder="your@email.com"
                className="w-full max-w-md bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            )}
          </div>

          {/* Triggers */}
          <div className="space-y-3">
            <p className="text-sm text-slate-400 font-medium">Notification Triggers</p>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.trigger_new_conversion}
                onChange={e => update({ trigger_new_conversion: e.target.checked })}
                className="h-4 w-4 accent-emerald-500 rounded"
              />
              <span className="text-sm">New conversion</span>
            </label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.trigger_value_exceeds}
                  onChange={e => update({ trigger_value_exceeds: e.target.checked })}
                  className="h-4 w-4 accent-emerald-500 rounded"
                />
                <span className="text-sm">Session value exceeds</span>
              </label>
              {settings.trigger_value_exceeds && (
                <div className="flex items-center gap-1">
                  <DollarSign className="h-4 w-4 text-slate-500" />
                  <input
                    type="number"
                    min={1}
                    value={settings.value_threshold}
                    onChange={e => update({ value_threshold: Number(e.target.value) })}
                    className="w-20 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                </div>
              )}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.trigger_daily_summary}
                onChange={e => update({ trigger_daily_summary: e.target.checked })}
                className="h-4 w-4 accent-emerald-500 rounded"
              />
              <span className="text-sm">Daily summary</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={settings.trigger_weekly_report}
                onChange={e => update({ trigger_weekly_report: e.target.checked })}
                className="h-4 w-4 accent-emerald-500 rounded"
              />
              <span className="text-sm">Weekly report</span>
            </label>
          </div>

          {/* Webhook URL */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-400 font-medium">
              <Webhook className="h-4 w-4" />
              Webhook URL (optional)
            </label>
            <input
              type="url"
              value={settings.webhook_url}
              onChange={e => update({ webhook_url: e.target.value })}
              placeholder="https://your-server.com/webhook"
              className="w-full max-w-lg bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            <p className="text-xs text-slate-600">POST request fired on each conversion event</p>
          </div>

          {/* Save */}
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Save Preferences
            </Button>
            {saved && (
              <span className="text-sm text-emerald-400 flex items-center gap-1">
                <CheckCircle className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </div>

        {/* Recent Notifications */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold">Recent Notifications</h2>
          <p className="text-sm text-slate-500">Last 10 conversion events</p>

          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />
              <span className="ml-2 text-slate-400">Loading...</span>
            </div>
          )}

          {!loading && conversions.length === 0 && (
            <p className="text-center py-10 text-slate-500">No conversion events yet.</p>
          )}

          {!loading && conversions.map((c, i) => (
            <div key={`${c.session_id}-${i}`} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50 border border-slate-700">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">
                  Conversion: {c.vertical_id ? (verticalMap[c.vertical_id] || c.vertical_id) : 'Unknown vertical'}
                </p>
                <p className="text-xs text-slate-500">Session: {c.session_id.slice(0, 8)}...</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-emerald-400">
                  ${(c.revenue || 0).toFixed(2)}
                </p>
                <p className="text-xs text-slate-500">
                  {new Date(c.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PortalLayout>
  );
}
